import React, { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { downloadToastOptions } from './downloadToast.options';
import { dismissableToastOptions } from './dismissableToast.options';
import { ToastVariant } from './ToastVariant';
import { makeStyles } from '~/UIKit/Style';

const useStyles = makeStyles(() => ({
  multilineEllipsis: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 10,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
  },
}));

export function useToastMessage(
  onCloseCallback?: (toastId: string, info?: { reason: string; event: any }) => void,
) {
  const { classes } = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t } = useTranslation();

  useEffect(() => {
    const key = enqueueSnackbar('invisible', { style: { visibility: 'hidden' } });
    closeSnackbar(key);
  }, []);

  const showToast = (
    toastId: string,
    message: React.ReactNode,
    variant: ToastVariant = ToastVariant.INFO,
    dismissable: boolean = false,
    callback?: (toastId: string) => void,
    duration: number = 3500,
  ) => {
    const content = <div className={classes.multilineEllipsis}>{message}</div>;

    if (dismissable) {
      enqueueSnackbar(
        content,
        dismissableToastOptions(
          duration,
          toastId,
          variant,
          t,
          (event, reason, key) => {
            callback && callback(toastId);
            onToastShown(event, reason, key);
          },
          closeSnackbar,
        ),
      );
    } else {
      enqueueSnackbar(content, {
        autoHideDuration: duration,
        key: toastId,
        variant,
        preventDuplicate: true,
        onClose: onToastShown,
      });
    }
  };

  const showDownloadToast = (
    toastId: string,
    message: string,
    link: string,
    jobId: string,
    markJobDone: (jobId: string) => void,
    variant: ToastVariant = ToastVariant.SUCCESS,
  ) => {
    const content = <div className={classes.multilineEllipsis}>{message}</div>;

    enqueueSnackbar(
      content,
      downloadToastOptions(toastId, link, variant, t, onToastShown, () => {
        closeSnackbar();
        markJobDone(jobId);
      }),
    );
  };

  const dismissToast = (key: string) => {
    closeSnackbar(key);
  };

  const onToastShown = (event: any, reason: string, key: string | number | undefined) => {
    if (onCloseCallback) {
      onCloseCallback(`${key}`, {
        reason,
        event,
      });
    }
  };

  return {
    showToast,
    showDownloadToast,
    dismissToast,
  };
}
